import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  approved: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  rejected: "bg-red-100 text-red-800 hover:bg-red-100",
};

export default function DiscountRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => api.get("/discount-requests", {
    params: { status: status === "All" ? undefined : status },
  }).then(r => setRows(r.data));

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [status]);

  const approve = async (row) => {
    try {
      await api.post(`/discount-requests/${row.id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const openReject = (row) => { setRejecting(row); setRejectReason(""); };
  const submitReject = async () => {
    try {
      await api.post(`/discount-requests/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejecting(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">Approvals</div>
        <h1 className="font-display text-4xl tracking-tight" data-testid="discount-requests-title">Discount requests</h1>
      </div>

      <Card className="p-4 mb-4 border-border shadow-sm">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="dq-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["pending", "approved", "rejected", "All"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Cashier</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Approved / Rejected by</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`dq-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-ZA")}</TableCell>
                  <TableCell className="text-sm">{r.cashier_name}</TableCell>
                  <TableCell className="text-xs uppercase">{r.discount_type}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.discount_type === "percent" ? `${r.discount_value}%` : currency(r.discount_value)}
                  </TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_TONE[r.status] || ""}>{r.status}</Badge>
                    {r.consumed_at && <span className="ml-2 text-[10px] text-muted-foreground">used</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.approved_by_name || "—"}
                    {r.rejection_reason && <div className="text-[10px] text-[#C05746]">&ldquo;{r.rejection_reason}&rdquo;</div>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => approve(r)} data-testid={`approve-${r.id}`}>
                          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-[#C05746]" onClick={() => openReject(r)} data-testid={`reject-${r.id}`}>
                          <XCircle className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                    {r.status === "pending" && !r.approved_by_name && (
                      <Clock className="w-3 h-3 text-amber-600 inline-block" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No requests.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reject-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Reject discount request</DialogTitle></DialogHeader>
          <Input placeholder="Reason (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button className="rounded-full bg-[#C05746] hover:bg-[#a94a3d]" onClick={submitReject} data-testid="submit-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
